import type { FormikConfig, FormikHelpers, FormikValues } from "formik";
import { Formik, useFormikContext } from "formik";
import { isObject } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useUniqueId } from "#src/hooks/use-unique-id";

import * as sx from "./form.sx";
import { preventEnterCallback } from "./utils";

export interface FormProps<
  Values extends FormikValues = FormikValues,
> extends FormikConfig<Values> {
  className?: string;
  "data-testid"?: string;
  id?: string;
  readonly?: boolean;
  style?: React.CSSProperties;
  submitOnEnter?: boolean;
}

const formConfigurationContext = createContext<{
  id: string;
  readonly?: boolean;
  submitOnEnter?: boolean;
}>({
  id: "",
});
export const useFormConfiguration = () => useContext(formConfigurationContext);

function FormContent<Values extends FormikValues = FormikValues>({
  id: inputId,
  "data-testid": dataTestId,
  children,
  className,
  style,
  submitOnEnter,
  readonly,
}: Omit<FormProps<Values>, "onSubmit"> & { children: React.ReactNode }) {
  const formId = useUniqueId(inputId);
  const contextValue = useMemo(
    () => ({ id: formId, readonly, submitOnEnter }),
    [formId, readonly, submitOnEnter]
  );

  return (
    <div
      data-testid={dataTestId}
      className={className}
      role="form"
      style={style}
      onKeyDown={preventEnterCallback}
    >
      <sx.Form id={formId} />
      <formConfigurationContext.Provider value={contextValue}>
        <MarkFieldsTouchedOnSubmit />
        {children}
      </formConfigurationContext.Provider>
    </div>
  );
}

export function Form<Values extends FormikValues = FormikValues>({
  onSubmit,
  children,
  ...props
}: FormProps<Values>) {
  // Formik manages isSubmitting state for async onSubmit handlers, but not sync ones
  // this dummy wrapper forces all handlers to be async so you don't have to call `setSubmitting(false)`
  // https://formik.org/docs/api/formik#onsubmit-values-values-formikbag-formikbag--void--promiseany
  const onSubmitCallback = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) =>
      Promise.resolve(onSubmit(values, formikHelpers)),
    [onSubmit]
  );

  return (
    <Formik {...props} onSubmit={onSubmitCallback}>
      {(formikProps) => (
        <FormContent<Values> {...props}>
          {typeof children === "function" ? children(formikProps) : children}
        </FormContent>
      )}
    </Formik>
  );
}

/**
 * Formik does not set `touched` during submission on fields that are not
 * present in the form's initial values. This is problematic for dynamic forms.
 * This workaround was adapted from the following comment on the relevant issue:
 *
 * https://github.com/jaredpalmer/formik/issues/445#issuecomment-677592750
 */
const MarkFieldsTouchedOnSubmit = () => {
  const { errors, setFieldTouched, isSubmitting } = useFormikContext();

  /**
   * isSubmitting will be toggled off in the same render cycle as errors are populated
   *
   * so, we need to track isSubmitting from the previous render cycle to
   * determine if errors were generated from the attempted submission
   *
   * use state with a useEffect to create a delayed synchronization
   */
  const [wasSubmitting, setWasSubmitting] = useState(isSubmitting);

  useEffect(() => {
    if (wasSubmitting) {
      getPaths(errors).forEach((path) => {
        setFieldTouched(path, true, false);
      });
    }
  }, [errors, wasSubmitting, setFieldTouched]);

  useEffect(() => {
    setWasSubmitting(isSubmitting);
  }, [isSubmitting]);

  return null;
};

function getPaths(object: any, prefix: string = "") {
  const paths: string[] = [];

  if (Array.isArray(object)) {
    object.forEach((item, index) => {
      const path = `${prefix}[${index}]`;
      paths.push(...getPaths(item, path));
    });
  } else if (isObject(object)) {
    Object.entries(object).forEach(([key, value]) => {
      const path = prefix === "" ? key : `${prefix}.${key}`;
      paths.push(...getPaths(value, path));
    });
  } else {
    paths.push(prefix);
  }

  return paths;
}
